import React, { useEffect, useState } from "react";
import CalendlyBookingWidget from "@/src/components/Calendly/CalendlyBookingWidget";
import { useCustomer } from "@/src/provider/Customer/CustomerProvider";
import { AbstractCase } from "@/src/types/Entities/AbstractCase";
import ApiClient from "@/src/services/apiClient";
import { getCalendlyDataFromServiceWorldCalendlyUrl } from "@/src/services/calendlyService";
import { Navigate, useNavigate } from "react-router";
import { getCaseLink } from "@/src/services/productService";
import { Box, Stack, TextField, Typography } from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import ButtonLoading from "@/src/components/Button/ButtonLoading";
import InquiryToCustomer, { InquiryToCustomerStatus } from "@/src/types/Entities/InquiryToCustomer";
import { getCustomerConsent } from "@/src/services/customerService";
import RecordingConsentForm from "@/src/components/Form/Common/RecordingConsent/RecordingConsentForm";
import ContentBox from "@/src/components/Box/ContentBox/ContentBox";
import InquiryToCustomerMediaObjects from "@/src/components/Serviceworld/InquiryToCustomer/InquiryToCustomerMediaObjects";
import { useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/src/services/reactQuery/reactQueryService";

type InquiryToCustomerAppointmentBoxProps = {
  product: AbstractCase;
  inquiryToCustomer: InquiryToCustomer;
  updateInquiryToCustomer: (data: Partial<InquiryToCustomer>) => Promise<InquiryToCustomer>;
};

export default function InquiryToCustomerAppointmentBox({ product, inquiryToCustomer, updateInquiryToCustomer }: InquiryToCustomerAppointmentBoxProps) {
  const { customer } = useCustomer();
  const [calendlyLink, setCalendlyLink] = useState<string>("");
  const [denyAppointment, setDenyAppointment] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [checkError, setCheckError] = useState<boolean>(false);
  const [inquiryTextAppointmentCustomer, setInquiryTextAppointmentCustomer] = useState<string>("");
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const refreshInquiryToCustomer = async () => {
    await queryClient.invalidateQueries({
      queryKey: queryKeys.all("/inquiry_to_customers"),
    });
  };

  const consent = customer ? getCustomerConsent(customer, "callRecordingAndSummary") : null;

  useEffect(() => {
    ApiClient.get(`/get_inquiry_calendly_link/${inquiryToCustomer.id}`).then((result) => setCalendlyLink(result));
  }, []);

  if (consent === null) {
    return <RecordingConsentForm handleConsent={(state: string) => {}} />;
  }

  if (!customer || !calendlyLink) {
    return null;
  }

  const { appointmentType, responsibleParty, utmSource } = getCalendlyDataFromServiceWorldCalendlyUrl(calendlyLink);

  if (!appointmentType || !responsibleParty || !utmSource) {
    return <Navigate to={getCaseLink(product)} />;
  }

  const handleSubmitDeny = async () => {
    if (inquiryTextAppointmentCustomer.length < 5) {
      setCheckError(true);
      return;
    }
    setIsLoading(true);
    await updateInquiryToCustomer({
      status: InquiryToCustomerStatus.STATUS_DONE,
      doneBy: "customer",
      inquiryTextAppointmentCustomer: inquiryTextAppointmentCustomer,
    });
    await refreshInquiryToCustomer();
    setIsLoading(false);

    navigate(getCaseLink(product) + "/anfrage/beantwortet");
  };
  return (
    <>
      <ContentBox headline={"Ihre Terminbuchung"}>
        {(inquiryToCustomer.inquiryTextAppointment || inquiryToCustomer.mediaObjects?.length > 0) && (
          <Stack spacing={2}>
            {inquiryToCustomer.inquiryTextAppointment && (
              <Box>
                <Typography variant={"body2"} sx={{ marginTop: "1.5rem" }}>
                  <strong>Wichtige Hinweise zu Ihrem Termin:</strong>
                </Typography>
                <Box sx={{ all: "initial" }}>
                  <Typography sx={{ color: "text.primary" }} dangerouslySetInnerHTML={{ __html: inquiryToCustomer.inquiryTextAppointment }} />
                </Box>
              </Box>
            )}
            {inquiryToCustomer.mediaObjects?.length > 0 && (
              <Box>
                <Typography variant={"body2"} sx={{ marginBottom: "1.5rem" }}>
                  <strong>Bitte berücksichtigen Sie auch {inquiryToCustomer.mediaObjects?.length === 1 ? "das Dokument" : "die Dokumente"}:</strong>
                </Typography>
                <InquiryToCustomerMediaObjects inquiryToCustomer={inquiryToCustomer} />
              </Box>
            )}
          </Stack>
        )}
        <Stack direction={"row"} sx={{ marginTop: "1.5rem", alignItems: "center" }}>
          <Checkbox sx={{ marginRight: "1rem" }} checked={denyAppointment} onChange={() => setDenyAppointment(!denyAppointment)} />
          <Typography>Das Anliegen hat sich erledigt oder der Termin ist aus anderen Gründen nicht (mehr) notwendig</Typography>
        </Stack>
        {!denyAppointment ? (
          <CalendlyBookingWidget
            appointmentType={appointmentType}
            responsibleParty={responsibleParty}
            customer={customer}
            product={product}
            utmSource={utmSource}
            onBookingScheduled={refreshInquiryToCustomer}
          />
        ) : (
          <Box sx={{ margin: "2rem 0" }}>
            <TextField
              fullWidth={true}
              label="Ihre Anmerkungen"
              name="inquiryTextAppointmentCustomer"
              value={inquiryTextAppointmentCustomer}
              onChange={({ target }) => {
                setCheckError(false);
                setInquiryTextAppointmentCustomer(target.value);
              }}
              onBlur={() => setCheckError(true)}
              placeholder={"Bitte teilen Sie hier kurz mit, warum der Termin Ihrer Meinung nach nicht (mehr) notwendig ist."}
              error={checkError && inquiryTextAppointmentCustomer.length < 5}
              helperText={checkError && inquiryTextAppointmentCustomer.length < 5 && "Bitte teilen Sie uns mit, warum der Termin nicht nötig ist"}
              multiline
              rows={5}
              slotProps={{
                htmlInput: {
                  maxLength: 1000,
                },

                inputLabel: {
                  shrink: true,
                },
              }}
            />
            <ButtonLoading sx={{ marginTop: "2rem" }} fullWidth={true} variant={"contained"} onClick={handleSubmitDeny} isLoading={isLoading}>
              Terminanfrage ablehnen
            </ButtonLoading>
          </Box>
        )}
      </ContentBox>
    </>
  );
}
